import SaveBar from '@/components/forms/SaveBar'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import { serverFieldErrors, useAppForm } from '@/lib/form'
import {
  useCreateDomainMutation,
  useDeleteDomainMutation,
  useUpdateDomainMutation,
  useVerifyDomainMutation,
} from '@/lib/services/domains/domains.query'
import type {
  Domain,
  DomainDnsRecord,
  DomainVerification,
} from '@/lib/services/domains/domains.types'
import { getErrorMessage } from '@/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { ArrowRight, Check, Info, ShieldCheck, ShieldMinus, Wallet, XCircle } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { parse } from 'tldts'
import DomainRecordTable from '../domains/DomainRecordTable'
import StripePaymentMethodDomainCard from '../domains/StripePaymentMethodDomainCard'
import { getStripePaymentMethodDomainStatusMeta } from '../domains/stripe-payment-method-domain-status'
import LoadingButton from '@/components/ui/loading-button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import { Badge } from '../ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Separator } from '../ui/separator'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import DeleteButton from './DeleteButton'

function getHelpfulMessage(record: DomainDnsRecord) {
  if (!record?.name) return null

  switch (record?.type) {
    case 'A':
    case 'CNAME':
      return `${record?.name} will not work`
    case 'TXT':
      return 'TXT record not found'
    default:
      return null
  }
}

function DnsResult({ domain, record }: { domain: Domain | undefined; record: DomainDnsRecord }) {
  const DNS_EXPECTED_VALUES = new Map<DomainDnsRecord['type'], string | null | undefined>([
    ['A', import.meta.env.VITE_DNS_IP_ADDRESS],
    ['CNAME', import.meta.env.VITE_DNS_URL],
    ['TXT', domain?.verification_token],
  ])

  const isVerified = record?.value === DNS_EXPECTED_VALUES.get(record?.type)

  return (
    <>
      <div className="col-span-1">{record.type}:</div>
      <div className="col-span-1">
        {isVerified ? (
          <Badge variant="success">Verified</Badge>
        ) : (
          <Badge variant="destructive">
            <XCircle /> Not Verified
          </Badge>
        )}
      </div>
      {!isVerified && (
        <div className="col-span-2 sm:col-span-3">
          <Typography variant="label">{getHelpfulMessage(record)}</Typography>
        </div>
      )}
      <Separator className="col-span-2 sm:col-span-5" />
    </>
  )
}

type DomainFormValues = Pick<Domain, 'domain' | 'redirect_apex' | 'is_primary'>

const initialData: DomainFormValues = {
  domain: '',
  redirect_apex: false,
  is_primary: false,
}

export function DomainForm({ domain }: { domain?: Domain }) {
  const isUpdate = domain !== undefined
  const updateMutation = useUpdateDomainMutation()
  const createMutation = useCreateDomainMutation()
  const formMutation = isUpdate ? updateMutation : createMutation

  const deleteDomainMutation = useDeleteDomainMutation({ cleanupOnSuccess: false })
  const { mutateAsync: verifyDomain, isPending } = useVerifyDomainMutation()

  const [showVerificationDialog, setShowVerificationDialog] = useState(false)
  const [verifyState, setVerifyState] = useState<DomainVerification | null>(null)

  const isVerifiedDomain = !!verifyState?.verified
  const stripeStatusMeta = isUpdate
    ? getStripePaymentMethodDomainStatusMeta(domain?.stripe_payment_method_domain)
    : null
  const stripeBadgeVariant =
    stripeStatusMeta?.variant === 'success' ? 'default' : stripeStatusMeta?.variant

  const navigate = useNavigate()
  const pathHelper = usePathHelper()

  const handleVerifyDomain = async (domainId: number) => {
    try {
      const response = await verifyDomain(domainId)
      setVerifyState(response)
      setShowVerificationDialog(true)
    } catch (error) {
      toast.error('Error attempting to verify domain', {
        description: getErrorMessage(error),
      })
    }
  }

  const defaultValues: DomainFormValues = isUpdate ? domain : initialData
  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        const action = isUpdate ? 'update' : 'create'
        const onSuccess = (savedDomain: Domain) => {
          toast.success(`Domain ${action}d successfully`)
          // Reset the form with the new data to update defaultValues
          formApi.reset()
          if (savedDomain?.id) {
            void navigate({
              ...pathHelper.domains.show(savedDomain?.id).link,
              ignoreBlocker: true,
            })
          }
        }
        try {
          if (isUpdate) {
            await updateMutation.mutateAsync({ ...value, id: domain.id }, { onSuccess })
          } else {
            await createMutation.mutateAsync(value, { onSuccess })
          }
          return undefined
        } catch (error) {
          toast.error(`Domain ${action} failed`, { description: getErrorMessage(error) })
          return serverFieldErrors(error)
        }
      },
    },
  })

  return (
    <>
      <AlertDialog open={showVerificationDialog} onOpenChange={setShowVerificationDialog}>
        <AlertDialogContent size="xlarge">
          <AlertDialogHeader>
            <AlertDialogTitle>Domain Record Verification</AlertDialogTitle>
            <AlertDialogDescription className="flex flex-col gap-y-4">
              <Typography variant="label">
                We've looked up your DNS records and these are the results.
              </Typography>
              <Typography variant="labelStrong">
                Note: if any of these are not verified then your customers may not be able to access
                your website!
              </Typography>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-y-4">
            <div className="col-span-1">Domain:</div>
            <div className="col-span-1">
              {isVerifiedDomain ? (
                <Badge variant="success">
                  <Check /> Verified
                </Badge>
              ) : (
                <>
                  <Badge variant="destructive">
                    <XCircle /> Not Verified
                  </Badge>{' '}
                </>
              )}
            </div>
            <div className="col-span-2 sm:col-span-3">
              <Typography variant="label">
                {!isVerifiedDomain && 'Your website is not accessible'}
              </Typography>
            </div>
            <Separator className="col-span-2 sm:col-span-5" />
            {verifyState?.records?.map((record) => (
              <DnsResult domain={domain} record={record} key={record.type} />
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogAction>Close</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Card className="max-w-4xl mb-0">
        <CardHeader>
          <CardTitle className="flex flex-wrap items-start gap-2">
            {isUpdate ? (
              <>
                <Badge
                  variant={domain?.verified_at ? 'default' : 'destructive'}
                  className="inline-flex items-center gap-2"
                >
                  {domain?.verified_at ? (
                    <ShieldCheck className="w-4 h-4" />
                  ) : (
                    <ShieldMinus className="w-4 h-4" />
                  )}
                  {domain?.verified_at ? 'Verified' : 'Not Verified'}
                </Badge>
                {stripeStatusMeta ? (
                  <Badge variant={stripeBadgeVariant} className="inline-flex items-center gap-2">
                    <Wallet className="w-4 h-4" />
                    {stripeStatusMeta.label}
                  </Badge>
                ) : null}
              </>
            ) : (
              <div>Create new domain</div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form.AppForm>
            <form.Form>
              <div className="flex flex-col gap-4">
                <form.AppField name="domain">
                  {(field) => (
                    <field.UrlInput id="domain" label="URL" type="url" placeholder="flowers.com" />
                  )}
                </form.AppField>
                <form.AppField name="is_primary">
                  {(field) => (
                    <div className="flex items-center gap-2">
                      <field.SwitchInput id="is_primary" label="Primary?" className="w-auto" />
                      <Tooltip>
                        <TooltipTrigger aria-label="About primary domains">
                          <Info size={16} className="text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          Redirect traffic from all other domains to this one?
                          <br />
                          Note: You must always have exactly one primary domain.
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                </form.AppField>
                <form.AppField name="redirect_apex">
                  {(field) => (
                    <form.Subscribe selector={(state) => state.values.domain}>
                      {(domainString) => {
                        const parsedDomain = parse(domainString)
                        return (
                          <>
                            {!!parsedDomain?.subdomain && (
                              <div className="inline-flex items-center gap-2">
                                <field.SwitchInput
                                  className="w-auto"
                                  id="redirect_apex"
                                  label="Redirect top level domain?"
                                  disabled={!parsedDomain?.subdomain}
                                />
                                <Tooltip>
                                  <TooltipTrigger aria-label="About redirecting the top level domain">
                                    <Info size={16} className="text-muted-foreground" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Redirect traffic from {parsedDomain?.domain}{' '}
                                    <ArrowRight size={16} className="inline mb-[1px]" />{' '}
                                    {parsedDomain?.hostname}? (most people want this)
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            )}
                            <div className="mt-8">
                              <DomainRecordTable
                                domain={domain}
                                parsedDomain={parsedDomain}
                                isUpdate={isUpdate}
                              />
                              {isUpdate ? (
                                <div className="mt-4">
                                  <LoadingButton
                                    size="sm"
                                    isPending={isPending}
                                    onClick={() => handleVerifyDomain(domain.id)}
                                  >
                                    Verify Records
                                  </LoadingButton>
                                </div>
                              ) : null}
                              {isUpdate ? (
                                <StripePaymentMethodDomainCard domain={domain} className="mt-8" />
                              ) : null}
                            </div>
                          </>
                        )
                      }}
                    </form.Subscribe>
                  )}
                </form.AppField>
              </div>
              <SaveBar mutation={formMutation} />
            </form.Form>
          </form.AppForm>
        </CardContent>
      </Card>
      {isUpdate && (
        <div className="max-w-4xl">
          <Can I="delete" a="domains">
            <DeleteButton
              id={domain?.id}
              model="Domain"
              mutation={deleteDomainMutation}
              path={pathHelper.domains.index()}
              replace
              onDeleted={deleteDomainMutation.cleanupDeletedDomain}
            />
          </Can>
        </div>
      )}
    </>
  )
}
